/**
 * useConnectionState — resolve a profile's ConnectionState for a card/gate.
 *
 * Prefers the locally-loaded graph (SocialGraphContext), falling back to an
 * authoritative single-profile fetch when the profile isn't in the loaded set.
 * Returns 'none' while `social.graph` is off, so cards degrade to a
 * no-connect-button projection.
 */

import { useEffect, useState } from 'react';
import { useSocialGraph } from '../context/SocialGraphContext';
import { useFeatureFlag } from './useFeatureFlag';
import { getConnectionState } from '../services/api/socialService';
import type { ConnectionState } from '../types/social';

export function useConnectionState(profileId: string | null | undefined): ConnectionState {
  const enabled = useFeatureFlag('social.graph');
  const { stateFor, connections, inbound, outbound } = useSocialGraph();
  const [resolved, setResolved] = useState<ConnectionState>('none');

  useEffect(() => {
    if (!enabled || !profileId) {
      setResolved('none');
      return;
    }
    // Local graph is authoritative when it knows the profile.
    const local = stateFor(profileId);
    if (local !== 'none') {
      setResolved(local);
      return;
    }
    // Otherwise confirm with the server (a profile met in a room may not be in
    // the loaded connection/request set).
    let cancelled = false;
    getConnectionState(profileId)
      .then((s) => {
        if (!cancelled) setResolved(s);
      })
      .catch(() => {
        if (!cancelled) setResolved('none');
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, profileId, stateFor, connections, inbound, outbound]);

  return resolved;
}

export default useConnectionState;
